import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js'
import config from '../config'

const userPool = new CognitoUserPool({
  UserPoolId: config.USER_POOL_ID,
  ClientId: config.CLIENT_ID,
})

// --- Sign In ---
export function signIn(email, password) {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    })

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken()
        const refreshToken = result.getRefreshToken().getToken()
        resolve({ idToken, refreshToken })
      },
      onFailure: (err) => {
        reject(err)
      },
      newPasswordRequired: (userAttributes) => {
        reject({ code: 'NewPasswordRequired', userAttributes })
      },
    })
  })
}

// --- Sign Up ---
export function signUp(email, password, name, companyName) {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
      new CognitoUserAttribute({
        Name: 'custom:plan',
        Value: 'free',
      }),
    ]

    userPool.signUp(email, password, attributes, null, (err, result) => {
      if (err) {
        reject(err)
        return
      }
      resolve({
        user: result.user,
        userConfirmed: result.userConfirmed,
        email,
        name,
        companyName,
      })
    })
  })
}

// --- Confirm Sign Up ---
export function confirmSignUp(email, code) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err)
        return
      }
      resolve(result)
    })
  })
}

// --- Resend Confirmation Code ---
export function resendConfirmationCode(email) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err)
        return
      }
      resolve(result)
    })
  })
}

// --- Forgot Password ---
export function forgotPassword(email) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.forgotPassword({
      onSuccess: (result) => resolve(result),
      onFailure: (err) => reject(err),
    })
  })
}

// --- Confirm Forgot Password ---
export function confirmForgotPassword(email, code, newPassword) {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

// --- Parse Cognito error messages ---
const COGNITO_ERROR_KEYS = {
  UserNotFoundException: 'accountLookupSafe',
  NotAuthorizedException: 'incorrectCredentials',
  UserNotConfirmedException: 'unconfirmed',
  UsernameExistsException: 'usernameExists',
  InvalidPasswordException: 'invalidPassword',
  CodeMismatchException: 'invalidCode',
  ExpiredCodeException: 'expiredCode',
  TooManyRequestsException: 'tooManyAttempts',
  LimitExceededException: 'tooManyAttempts',
}

const SAFE_ENGLISH_ERRORS = {
  accountLookupSafe: 'If an account exists for this email address, a reset code will be sent.',
  incorrectCredentials: 'Incorrect email or password.',
  unconfirmed: 'Your account is not verified yet. Enter the code from your email to continue.',
  usernameExists: 'An account already exists with this email address.',
  invalidPassword: 'Password must be at least 8 characters with uppercase, lowercase and numbers.',
  invalidCode: 'The verification code is incorrect. Please try again.',
  expiredCode: 'The verification code has expired. Please request a new one.',
  tooManyAttempts: 'Too many attempts. Please wait a moment and try again.',
  generic: 'Something went wrong. Please try again.',
}

export function isUserNotFoundError(err) {
  return err?.code === 'UserNotFoundException'
}

export function getCognitoErrorMessage(err, translate) {
  const key = COGNITO_ERROR_KEYS[err?.code] || 'generic'
  return typeof translate === 'function'
    ? translate(`auth.errors.${key}`)
    : SAFE_ENGLISH_ERRORS[key]
}
